import {boardCellKey} from '@github-ui/board-core'

import {normalizeItemMoves} from './board-move-axes'
import type {SavedViewsBoardAxisPage, SavedViewsBoardItem, SavedViewsBoardPayload} from './types'

export function appendAxisPage<TItem extends SavedViewsBoardItem, TMetadata, TPageMetadata>(
  payload: SavedViewsBoardPayload<TItem, TMetadata>,
  page: SavedViewsBoardAxisPage<TItem, TPageMetadata>,
  appendMetadata?: (metadata: TMetadata, page: SavedViewsBoardAxisPage<TItem, TPageMetadata>) => TMetadata,
): SavedViewsBoardPayload<TItem, TMetadata> {
  const {boardData} = payload
  const {data, rowField} = boardData
  const axis = page.axis === 'columns' ? data.columns : data.rows
  const heldAxisKeys = new Set(axis.map(value => value.key))
  const values = page.values.filter(value => !heldAxisKeys.has(value.key))
  const heldCellKeys = new Set(data.cells.map(boardCellKey))
  const cells = page.cells
    .filter(cell => !heldCellKeys.has(boardCellKey(cell)))
    .map(cell => ({...cell, items: cell.items.map(item => normalizeItemMoves(item, rowField != null))}))

  const cellCursors = new Map(boardData.cellCursors)
  for (const cell of cells) {
    const key = boardCellKey(cell)
    const cursor = page.cellCursors.get(key)
    if (cursor !== undefined) cellCursors.set(key, cursor)
  }

  const nextData =
    page.axis === 'columns'
      ? {
          ...data,
          columns: [...data.columns, ...values],
          cells: [...data.cells, ...cells],
          hasMoreColumns: page.hasMoreValues,
          columnsTruncated: page.truncated,
        }
      : {
          ...data,
          rows: [...data.rows, ...values],
          cells: [...data.cells, ...cells],
          hasMoreRows: page.hasMoreValues,
          rowsTruncated: page.truncated,
        }

  return {
    ...payload,
    boardData:
      page.axis === 'columns'
        ? {...boardData, data: nextData, cellCursors, columnsCursor: page.cursor}
        : {...boardData, data: nextData, cellCursors, rowsCursor: page.cursor},
    metadata: appendMetadata ? appendMetadata(payload.metadata, page) : payload.metadata,
  }
}
